import React from "react";
import {Tag, styled} from "@reside/ui";

import {TemplateResultInfo} from "../types";
import {ProcessingTimeline} from "./ProcessingTimeline";

function StatusTag({success}: {success: boolean | null}): JSX.Element {
  if (success === null) return <Tag color="gray100">N/A</Tag>; // eslint-disable-line no-restricted-syntax -- intentional: must distinguish null from false
  return success ? <Tag color="green100">OK</Tag> : <Tag color="deepOrange100">Failed</Tag>;
}

const Card = styled.div<{hasErrors: boolean}>`
  border: 1px solid ${({hasErrors}) => (hasErrors ? "#ff3a45" : "#ebedf0")};
  border-radius: 6px;
  padding: 14px 16px;
  background: ${({hasErrors}) => (hasErrors ? "#fff5f5" : "#fafcff")};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const TemplateName = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #203c63;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #596973;
`;

const ErrorList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
  font-size: 12px;
  color: #a10009;
`;

export const TemplateResultCard = ({
  result,
}: {
  result: TemplateResultInfo;
}): JSX.Element => {
  const hasErrors = result.errors !== null && result.errors.length > 0;
  const fileCount = result.files?.length ?? 0;

  return (
    <Card hasErrors={hasErrors}>
      <CardHeader>
        <TemplateName>{result.template_version}</TemplateName>
        <StatusTag success={result.success} />
      </CardHeader>
      <MetaRow>
        <span>{fileCount} file{fileCount !== 1 ? "s" : ""}</span>
        <span>Hash: {result.hash.substring(0, 7)}</span>
        {result.status && <span>Status: {result.status}</span>}
      </MetaRow>
      {hasErrors && (
        <ErrorList>
          {result.errors.map((error, i) => (
            // skipcq: JS-0437 -- index needed: error strings can be duplicates
            <li key={`${error}-${i}`}>{error}</li>
          ))}
        </ErrorList>
      )}
      <ProcessingTimeline result={result} />
    </Card>
  );
};
